import React, { useEffect, useState } from 'react';
import './index.css';
import { QuranProvider } from './contexts/QuranContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { GoalsProvider } from './contexts/GoalsContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ProgressBar from './components/ProgressBar';
import VerseDisplay from './components/VerseDisplay';
import NavigationControls from './components/NavigationControls';
import SearchBar from './components/SearchBar';
import useQuranReader from './hooks/useQuranReader';
import useVerseShortcuts from './hooks/useVerseShortcuts';
import useSwipe from './hooks/useSwipe';
import useScrollToVerse from './hooks/useScrollToVerse';
import { getRegistration } from './utils/push';
import GoalStrip from './components/goals/GoalStrip';
import GoalModal from './components/goals/GoalModal';
import ReminderBanner from './components/goals/ReminderBanner';
import ReadingTracker from './components/goals/ReadingTracker';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

// Main content wrapper component
const QuranReader = () => {
  const {
    currentSurahInfo,
    currentSurah,
    currentVerse,
    goToNextVerse,
    goToPreviousVerse,
    playAudio
  } = useQuranReader();

  // Keeps the verse in view when moving to one that is taller than the screen
  const verseCardRef = useScrollToVerse(`${currentSurah}:${currentVerse}`);

  useVerseShortcuts({
    onNext: goToNextVerse,
    onPrevious: goToPreviousVerse,
    onTogglePlay: playAudio
  });

  const [showGoalModal, setShowGoalModal] = useState(false);

  // Swiping left turns the page forward, as in a right-to-left book
  const swipeHandlers = useSwipe({
    onSwipeLeft: goToNextVerse,
    onSwipeRight: goToPreviousVerse
  });

  return (
    <div className="flex flex-col min-h-screen bg-soft-ivory">

      {/* Header */}
      <Header />

      {/* Main content */}
      <main className="flex-grow container mx-auto px-4 py-8 max-w-3xl">
        {/* Surah title */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-heading font-kufi">
            Surah {currentSurahInfo.number}: {currentSurahInfo.name}
          </h2>
          <p className="font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.7 }}>{currentSurahInfo.englishName}</p>
        </div>

        {/* Search Bar */}
        <SearchBar />

        {/* Reading goal */}
        <ReminderBanner onOpen={() => setShowGoalModal(true)} />
        <GoalStrip onOpen={() => setShowGoalModal(true)} />

        {/* Progress bars */}
        <ProgressBar />

        {/* Verse content */}
        <div
          className="mb-6 p-6 rounded-lg shadow-md border"
          style={{
            backgroundColor: 'var(--color-card-bg)',
            borderColor: 'var(--color-card-border)'
          }}
          {...swipeHandlers}
          ref={verseCardRef}
        >
          <VerseDisplay />
        </div>

        {/* Navigation buttons */}
        <NavigationControls />
      </main>
      
      {/* Footer */}
      <Footer />

      {/* Records what has actually been read; renders nothing */}
      <ReadingTracker />

      {showGoalModal && <GoalModal onClose={() => setShowGoalModal(false)} />}
    </div>
  );
};

function App() {
  // Initialize settings from localStorage
  useEffect(() => {
    // Font mapping
    const fontSizeValues = {
      'xs': '0.75rem',
      'sm': '0.875rem',
      'base': '1rem',
      'lg': '1.125rem',
      'xl': '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem'
    };
    
    // Initialize Arabic font
    const savedFont = localStorage.getItem('quranReaderArabicFont') || 'scheherazade';
    document.documentElement.setAttribute('data-arabic-font', savedFont);
    
    // Initialize font sizes
    const arabicFontSize = localStorage.getItem('quranReaderArabicFontSize') || '2xl';
    const translationFontSize = localStorage.getItem('quranReaderTranslationFontSize') || 'base';
    const transliterationFontSize = localStorage.getItem('quranReaderTransliterationFontSize') || 'base';
    
    document.documentElement.style.setProperty('--arabic-font-size', fontSizeValues[arabicFontSize] || '1.5rem');
    document.documentElement.style.setProperty('--translation-font-size', fontSizeValues[translationFontSize] || '1rem');
    document.documentElement.style.setProperty('--transliteration-font-size', fontSizeValues[transliterationFontSize] || '1rem');

    // The worker now serves the app shell offline, not only push, so it is
    // registered for everyone rather than only when notifications are enabled.
    getRegistration();
  }, []);
  
  return (
    <ThemeProvider>
      <GoalsProvider>
        <QuranProvider>
          <QuranReader />
          <Analytics />
          <SpeedInsights />
        </QuranProvider>
      </GoalsProvider>
    </ThemeProvider>
  );
}

export default App;